"use client";

import { useCallback, useState } from "react";

/**
 * Reviewer avatar: the real photo when present AND it actually loads, else an
 * on-brand initial circle.
 *
 * WHY THE ERROR PATH EXISTS (added 2026-08-09)
 * -------------------------------------------
 * The photo URL is a THIRD-PARTY one we do not control — `lh3.googleusercontent
 * .com/a/<opaque>` for a Google Business Profile / Featurable review, or an R2
 * key for an admin testimonial. This component already fell back to the initial
 * circle when `src` was ABSENT ("Google's API frequently returns a null photo"),
 * but not when `src` was PRESENT and failed to load — which happens routinely:
 *
 *   - the reviewer changes or deletes their Google profile photo and the opaque
 *     URL we cached in the review payload 404s;
 *   - a privacy extension (uBlock/Privacy Badger and friends) blocks
 *     `googleusercontent.com` outright — very common on the visitor side;
 *   - Google's CDN is briefly unreachable from the visitor's network.
 *
 * In every one of those cases the old code left a decoded-zero-width `<img>` in
 * the card: a 36px hole exactly where the doc-comment promised "every card
 * visually aligned instead of leaving a gap". Falling back on error is the same
 * decision the missing-`src` branch already makes, applied to the other way the
 * photo can be missing.
 *
 * IT MUST NOT BE A PURE `onError` HANDLER. This tree is server-rendered, so the
 * image starts fetching from the SSR HTML long before React hydrates — a failure
 * that lands in that window fires its `error` event with nobody listening, and
 * `onError` alone would never run. The ref callback re-derives the same fact from
 * the DOM at commit time (`complete && naturalWidth === 0` — a finished fetch
 * that decoded nothing), which is precisely the signal the E2E broken-image scan
 * uses.
 *
 * The failure is remembered as the URL that failed, not as a boolean: a boolean
 * would need clearing whenever `src` changed, and "reset state in an effect" is
 * both a cascading-render smell and the shape that leaves a stale `true` behind.
 * Comparing against the current `src` makes a new photo self-evidently unjudged.
 */
export function ReviewAvatar({ src, name }: { src?: string; name: string }) {
  const initial = name.trim().charAt(0).toUpperCase() || "★";
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const failed = !!src && failedSrc === src;

  // A lazy image that has not started fetching reports `complete === false`, so
  // this cannot false-positive on an image that is merely still off-screen.
  const detectPreHydrationFailure = useCallback(
    (img: HTMLImageElement | null) => {
      if (img && img.complete && img.naturalWidth === 0 && src) setFailedSrc(src);
    },
    [src]
  );

  if (src && !failed) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- reviewer photos are external Google/CMS URLs that cannot be safely configured for Next image optimisation.
      <img
        ref={detectPreHydrationFailure}
        src={src}
        alt=""
        loading="lazy"
        onError={() => setFailedSrc(src)}
        className="h-9 w-9 shrink-0 rounded-full object-cover ring-1 ring-border"
      />
    );
  }

  return (
    <span
      aria-hidden="true"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-mist font-serif text-sm font-semibold text-primary ring-1 ring-border"
    >
      {initial}
    </span>
  );
}
